import React, { useEffect, useState, useRef } from 'react';
import classNames from 'classnames';
import styles from './style.module.css';
import { applySpec, pipe, pathOr, reduce } from 'ramda';
import eye from '../../assets/images/eye.svg';
import ReactGA from 'react-ga';
import { useNavigate } from 'react-router-dom';
import DropdownMenu from '../DropdownMenu';
import Modal from '../Modal';
import successIcon from './sucesso.svg';
import warningIcon from './warning.svg';
import { deletePastDues } from '../../services/kollectaApi';
import { BiSolidUpArrow, BiSolidDownArrow } from 'react-icons/bi';

const Table = ({
  title,
  downloadText,
  tbSettings,
  data,
  handleButtonClick,
  user
}) => {
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);
  const [receivedData, setReceivedData] = useState(data);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [deletionLogId, setDeletionLogId] = useState(null);
  const deleteModalRef = useRef(null);

  const handleCellClick = (item) => {
    ReactGA.event({
      category: 'Botão',
      action: 'Clique',
      label: 'Modal de informações devedor'
    });
    setSelectedItem(item);
    if (item !== null) {
  navigate('/dashboard/details', { state: { data: item } });
}
  }
  
  const handleSort = (field) => {
    const sortableFields = ['updatedAt', 'amount', 'days'];
    if (sortableFields.includes(field)) {
      if (sortField === field && sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortField === field && sortDirection === 'desc') {
        setSortField(null);
        setSortDirection(null);
      } else {
        setSortField(field);
        setSortDirection('asc');
      }
    }
  };

  const getSortedData = () => {
    if (!sortField || !sortDirection) {
      return receivedData;
    }

    if (sortField === 'updatedAt') {
      return [...receivedData].sort((a, b) => {
        if (!a.updatedAt && !b.updatedAt) return 0;
        if (!a.updatedAt) return 1;
        if (!b.updatedAt) return -1;
        
        const dateA = new Date(a.updatedAt).getTime();
        const dateB = new Date(b.updatedAt).getTime();
        
        return sortDirection === 'asc' ? dateA - dateB : dateB - dateA;
      });
    }

    if (sortField === 'amount') {
      return [...receivedData].sort((a, b) => {
        const valueA = parseFloat(a.amount ?? a.discountAmount ?? 0);
        const valueB = parseFloat(b.amount ?? b.discountAmount ?? 0);
        
        return sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
      });
    }

    if (sortField === 'days') {
      return [...receivedData].sort((a, b) => {
        let daysA = null;
        if (a.status !== 'paid' && a.validDueDate) {
          const dueDateA = new Date(a.validDueDate);
          const today = new Date();
          if (dueDateA < today) {
            const timeDiff = today - dueDateA;
            daysA = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
          }
        }

        let daysB = null;
        if (b.status !== 'paid' && b.validDueDate) {
          const dueDateB = new Date(b.validDueDate);
          const today = new Date();
          if (dueDateB < today) {
            const timeDiff = today - dueDateB;
            daysB = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
          }
        }

        if (daysA === null && daysB === null) return 0;
        if (daysA === null) return 1;
        if (daysB === null) return -1;

        return sortDirection === 'asc' ? daysA - daysB : daysB - daysA;
      });
    }

    return receivedData;
  };

  const buildItem = (acc, next) => {
    if (!acc[next.path]) {
      acc = {
        ...acc,
        [next.path]: (item) => {
          if (!item) {
            return (
              <td key={next.path} className={styles.tbCel}>
                -
              </td>
            );
          }

          if (next.path === 'days') {
            if (item.status === 'paid') {
              return (
                <td key={`${next.path}-paid`} className={styles.tbCel}>
                  -
                </td>
              );
            }
  
            const dueDate = item.validDueDate ? new Date(item.validDueDate) : null;
            const today = new Date();
            let overdueDays = null;
  
            if (dueDate && dueDate < today) {
              const timeDiff = today - dueDate;
              overdueDays = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
            }
  
            return (
              <td key={`${next.path}-${overdueDays}`} className={styles.tbCel}>
                {overdueDays !== null ? `${overdueDays} dias` : '-'}
              </td>
            );
          }

          if (next.path === 'details') {
            return (
              <td key={`${next.path}-details`} className={styles.details}>
                <img
                  onClick={() => handleCellClick(item)}
                  src={eye}
                  alt="Botão para página de detalhe"
                />
              </td>
            );
          }

          const value = next.columnFormat
            ? next.columnFormat(item)
            : pathOr('-', [next.path], item);
  
          return (
            <td
              key={`${next.path}-${value}`}
              className={classNames([
                styles.tbCel,
                { [styles.tbAlignCenter]: next.center },
                next.path === 'details' ? styles.cursor : '',
                next.path === 'customerName' ? styles.customerName : '',
              ])}
              onClick={next.path === 'details' ? () => handleCellClick(item) : null}
            >
              {value}
            </td>
          );
        },
      };
    }
    return acc;
  };
  
  const buildTdItem = (item) => {
    let items = []
    for(let key in item) {
      items = [...items, item[key]]
    }
    return items
  }
  
  const buildCollumn = (settings, receivedData) => {
    const paths = pipe(reduce(buildItem, {}))(settings);
    const sortedData = getSortedData();
    const result = sortedData
      .map(applySpec(paths))
      .map(buildTdItem);
      return result;
  }

  const buildDataTb = buildCollumn(tbSettings, receivedData);

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
    setTimeout(() => {
      deleteModalRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const handleConfirmDelete = async () => {
    setShowDeleteModal(false);
    const pastDueIds = data.map(item => item.id);
    try {
      const response = await deletePastDues(pastDueIds);
      if (response.status === 200) {
        setDeletionLogId(response.data?.deletionLogId);
        setShowSuccessModal(true);
      } else {
        console.error('Erro ao deletar a base:', response.data);
      }
    } catch (error) {
      console.error('Erro ao deletar a base:', error);
    }
  };

  const handleCloseSuccess = () => {
    setShowSuccessModal(false);
  };

  const RenderHeaders = ({ headerName, center, path }, index) => {
    if (path === 'details') {
      return (
        <th
          key={`${headerName}-${index}`}
          className={classNames([
            styles.tbCel,
            { [styles.tbAlignCenter]: center },
          ])}
        >
         {user && user?.type !== 'operator' && <DropdownMenu queueFunction={handleButtonClick} deleteFunction={handleDeleteClick} />}
        </th>
      );
    }

    const sortableFields = ['updatedAt', 'amount', 'days'];
    if (sortableFields.includes(path)) {
      const isActive = sortField === path;
      return (
        <th 
          key={`${headerName}-${index}`}
          onClick={() => handleSort(path)}
          className={classNames([
            styles.tbCel,
            { [styles.tbAlignCenter]: center },
          ])}
          style={{ cursor: 'pointer' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>{headerName}</span>
            <div className={styles.sortIcons}>
  <BiSolidUpArrow 
    className={styles.sortIcon} 
    size={12} 
  />
  <BiSolidDownArrow 
    className={styles.sortIcon} 
    size={12} 
  />
</div>
          </div>
        </th>
      );
    }
  
    return (
      <th 
        key={`${headerName}-${index}`}
        className={classNames([
          styles.tbCel,
          { [styles.tbAlignCenter]: center },
        ])}
      >
        {headerName}
      </th>
    );
  }

  useEffect(() => {
    setReceivedData(data);
    setSortField(null);
    setSortDirection(null);
  }, [data]);

  return (
    <div className={styles.tableContainer}>
      <table className={styles.tableCustom}>
        <thead className={styles.tbHeader}>
          <tr className={handleButtonClick && styles.containerWithButton}>
            {tbSettings.map(RenderHeaders)}
          </tr>
        </thead>
        <tbody>
          {buildDataTb.map((item, index) => (
            <tr key={index}>
              {item}
            </tr>
          ))}
        </tbody>
      </table>
      {showDeleteModal && (
        <Modal onClose={() => setShowDeleteModal(false)}>
          <div ref={deleteModalRef} className={styles.modalContainer} style={{textAlign: 'center'}}>
            <img src={warningIcon} alt="Atenção" style={{width: 60, margin: '0 auto 16px'}} />
            <h3 style={{margin: 0, fontWeight: 700}}>Tem certeza que deseja deletar a base?</h3>
            <p style={{margin: '8px 0 24px', color: '#888'}}>Essa é uma ação irreversível.</p>
            <div style={{display: 'flex', gap: 16, justifyContent: 'center'}}>
              <button style={{background: '#2ecc40', color: '#fff', border: 'none', borderRadius: 4, padding: '10px 32px', fontWeight: 700, fontSize: 16, cursor: 'pointer'}} onClick={handleConfirmDelete}>Sim</button>
              <button style={{background: '#e53935', color: '#fff', border: 'none', borderRadius: 4, padding: '10px 32px', fontWeight: 700, fontSize: 16, cursor: 'pointer'}} onClick={() => setShowDeleteModal(false)}>Não</button>
            </div>
          </div>
        </Modal>
      )}
      {showSuccessModal && (
        <Modal onClose={handleCloseSuccess}>
          <div className={styles.modalContainer} style={{textAlign: 'center'}}>
            <img src={successIcon} alt="Sucesso" style={{width: 60, margin: '0 auto 16px'}} />
            <h3 style={{margin: 0, fontWeight: 700, color: '#2ecc40'}}>Solicitação de deleção enviada com sucesso!</h3>
            <p style={{margin: '8px 0 0', color: '#888'}}>O processo de deleção está acontecendo e demora alguns minutos para terminar.</p>
            <p style={{margin: '8px 0 0', color: '#888'}}>Identificador da solicitação: {deletionLogId}</p>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default Table;